<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: BlogOverview                                          |
| Section: views/blog                                              |
| Purpose: Blog index page with localized story cards.             |
+------------------------------------------------------------------+
-->
<template>
    <div class="BlogOverview_wrapper wrapper">
        <h1>{{ storyTitle }}</h1>
        <p class="subtitle">{{ $t('blogOverview.subtitle') }}</p>
        <div class="StoryCard_wrapper">
            <LoadingSpinner v-if="loading" />
            <template v-else>
                <StoryCard v-for="(post, index) in posts" :key="post.id" :titleGroupTitle="post.title"
                    :description="post.summary" :imageSrc="post.image || defaultImageSrc"
                    :buttonLabel="$t('blogOverview.readMore')" :buttonHref="localePath(`/verhalen/${post.slug}`)"
                    :reverse="index % 2 === 1" :transparentPhotoBackground="true" />
            </template>
        </div>
        <!-- <div class="quote_wrapper">
            <hr>
            <p><strong>Dood ben ik pas als jullie mij vergeten</strong></p>
            <hr>
        </div> -->
    </div>

    <ContactCta />
</template>


<script>
import { useLocale } from '@/composables/useLocale';
import StoryCard from '@/components/StoryCard.vue';
import ContactCta from '@/components/ContactCta.vue';
import { applyGenericSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { markMachineTranslationDetected } from '@/utils/machineTranslationNotice';

export default {
    components: {
        StoryCard,
        ContactCta,
        LoadingSpinner,
    },
    setup() {
        const { localePath } = useLocale();
        return { localePath };
    },
    data() {
        return {
            posts: [],
            localizedContentLocale: null,
            loading: true,
        };
    },
    computed: {
        storyTitle() {
            const count = this.posts.length;
            return `${count} ${count === 1 ? this.$t('blogOverview.storyCount.one') : this.$t('blogOverview.storyCount.other')}`;
        },
        defaultImageSrc() {
            return `${process.env.VUE_APP_STRAPI_API_URL.replace('/api', '')}/uploads/sejarah_thumann_f44b3c03a9.jpg`;
            // return require('@/assets/sejarah_thumann1080x781.jpg');
        }
    },
    created() {
        this.loadLocaleData(this.$i18n.locale);
    },
    watch: {
        '$i18n.locale'(newLocale) {
            this.loadLocaleData(newLocale);
        },
        '$route.fullPath'() {
            this.markLocalizedContentDetected();
        },
    },
    mounted() {
        const title = "Verhalen | Sejarah Thümann";
        const description = "Lees indrukwekkende familieverhalen uit Nederlands-Indië. Ontdek het koloniale verleden door persoonlijke verhalen, foto's, historische documenten en archiefstukken.";
        const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg"; // of een andere relevante blog image
        const url = window.location.href;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });
        applyBreadcrumbSeo([{ name: 'Home', url: 'https://sejarah-thumann.online/' }, { name: 'Verhalen', url: 'https://sejarah-thumann.online/verhalen' }]);
    },
    methods: {
        loadLocaleData(locale) {
            this.loading = true;
            this.localizedContentLocale = null;
            try {
                try {
                    this.posts = require(`@/data/getBlogPosts/posts_${locale}.json`).data;
                    if (locale !== 'nl') {
                        this.localizedContentLocale = locale;
                        this.markLocalizedContentDetected();
                    }
                } catch {
                    this.posts = require('@/data/getBlogPosts/posts.json').data;
                }
            } catch (error) {
                console.error('Error loading local JSON:', error);
            } finally {
                this.loading = false;
            }
        },
        markLocalizedContentDetected() {
            if (this.localizedContentLocale === this.$i18n.locale) {
                markMachineTranslationDetected();
            }
        },
    },
};

</script>

<style scoped>
.BlogOverview_wrapper {
    padding-top: 144px;
}

h1,
.subtitle {
    text-align: left;
    padding: 0 20px;
    margin-bottom: 16px;
}

.subtitle {
    margin-bottom: calc(16px + 16px);
}

.quote_wrapper {
    padding: 0 20px 144px 20px;
    margin: auto;
}

@media (max-width: 860px) {
    .BlogOverview_wrapper {
        padding-top: 72px;
    }

    .quote_wrapper {
        padding: 0 20px 72px 20px;
    }
}
</style>
